import React, { useState } from 'react'
import styled from 'styled-components'
import copy from 'copy-to-clipboard'
import { useToast } from '@sketch/toasts'
import {
  Box,
  Input,
  TextArea,
  Checkbox,
  Form,
  Text,
  PasswordInput,
  generatePassword,
  Flex,
  FlatButton,
} from '@sketch/components'
import { usePreviewForm } from './usePreviewForm'
import { PreviewLinkSection } from './PreviewLinkSection'
import { DatePicker } from '../../../activity/components/Notifications/NotificationSearch/NotificationSearch.styles'

const DatePickerStyled = styled(DatePicker)`
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  font-size: ${({ theme }) => theme.fontSizes.E};
`

const PasswordToggle = styled(FlatButton)`
  padding: 0 12px;
  margin-top: 1px; /* stylelint-disable-line scales/space */
  height: 22px;
  font-size: ${({ theme }) => theme.fontSizes.D};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  text-decoration: underline;
`

interface SettingsTabProps {
  shareName: string
  isEditMode: boolean
}

export const SettingsTab = ({ shareName, isEditMode }: SettingsTabProps) => {
  const [showPasswordInput, setShowPasswordInput] = useState(!isEditMode)
  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    initialValues,
  } = usePreviewForm()
  const { showToast } = useToast()

  const handleOnCopy = () => {
    copy(values.password)
    showToast('Password copied')
  }

  const handleOnGeneratePassword = () => {
    setFieldValue('password', generatePassword(12))
  }

  return (
    <>
      {isEditMode ? (
        <PreviewLinkSection />
      ) : (
        <Text>
          Create a preview to share only specific pages and prototypes of{' '}
          <strong>&quot;{shareName}&quot;</strong>.
        </Text>
      )}
      <Form.Field
        name="previewName"
        label="Preview Name"
        errorText={touched.previewName ? errors.previewName : undefined}
      >
        <Input
          type="text"
          name="previewName"
          placeholder="Give this preview a name..."
          value={values.previewName}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Field>

      <Form.Field name="description" label="Description (Optional)">
        <TextArea
          name="description"
          placeholder="This preview is for..."
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={255}
        />
      </Form.Field>
      <Box mb={2}>
        <Text mb={2}>Preview Options</Text>
        <Box mb={2}>
          <Checkbox
            name="inspectEnabled"
            checked={values.inspectEnabled}
            onChange={handleChange}
          >
            Allow inspecting and asset downloads
          </Checkbox>
        </Box>
        <Box mb={2}>
          <Checkbox
            name="commentsEnabled"
            checked={values.commentsEnabled}
            onChange={handleChange}
          >
            Allow commenting with Sketch account
          </Checkbox>
        </Box>
        <Box mb={2}>
          <Flex alignItems="center">
            <Checkbox
              name="passwordProtected"
              checked={values.passwordProtected}
              onChange={e => {
                // when the preview previously wasn't password protected
                if (!initialValues.passwordProtected) {
                  setShowPasswordInput(!showPasswordInput)
                  handleChange(e)
                  return
                }

                if (showPasswordInput) {
                  setShowPasswordInput(false)
                  setFieldValue('password', '')
                  setFieldTouched('password', false)
                }

                handleChange(e)
              }}
            >
              Protect with password
            </Checkbox>
            {values.passwordProtected && !showPasswordInput && (
              <PasswordToggle onClick={() => setShowPasswordInput(true)}>
                Create New Password
              </PasswordToggle>
            )}
          </Flex>
          {showPasswordInput && values.passwordProtected && (
            <Box mt={1} ml={7}>
              <Form.Field
                name="password"
                errorText={touched.password ? errors.password : undefined}
              >
                <PasswordInput
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onPwdCopy={handleOnCopy}
                  onGeneratePassword={handleOnGeneratePassword}
                />
              </Form.Field>
            </Box>
          )}
        </Box>
        <Box mb={2}>
          <Checkbox
            name="expiryDateEnabled"
            checked={values.expiryDateEnabled}
            onChange={handleChange}
          >
            Expire preview link on…
          </Checkbox>
          {values.expiryDateEnabled && (
            <Box mt={1} ml={7} width={150}>
              <DatePickerStyled
                name="expiryDate"
                type="date"
                placeholder="Expire on"
                aria-label="Expire on"
                value={values.expiryDate}
                isDarkMode={false}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}
